'use client';
import { cinzel, merriweather } from '@/app/fonts';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/Auth';
import { Logo } from '../logo';


// this will view as the landing page and the home page inside the dashboard
export default function HomeSection() {
  const router = useRouter();
  const pathname = usePathname();
  const { idTokenPayload } = useAuth();

  // Real <a href> to the public route so crawlers can follow it, with a click
  // handler that sends signed-in members to the member route instead.
  // Modifier-clicks fall through so cmd/ctrl-click still opens a new tab.
  const cardLink = (publicPath: string, memberPath: string) => ({
    href: publicPath,
    onClick: (e: React.MouseEvent) => {
      if (!idTokenPayload) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
        return;
      e.preventDefault();
      router.push(memberPath);
    },
  });

  const isLanding = pathname === '/';

  const titleSizeCls = isLanding
    ? 'text-[19px] 2xl:text-[35px] 4xl:text-[44px]'
    : 'text-[17px] md:text-[16px] lg:text-[18px] 2xl:text-[26px] 4xl:text-[30px]';
  const bodySizeCls = isLanding
    ? 'text-[12px] xl:text-[14px] 2xl:text-[22px] 4xl:text-[38px]'
    : 'text-xs md:text-[11px] lg:text-[12px] xl:text-[13px] 2xl:text-base 4xl:text-[22px]';

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const hoverVariants = {
    hover: { scale: 1.02, y: -5 },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
      },
    },
  };

  const imageVariants = {
    hover: { scale: 1.05 },
  };

  const textVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <motion.div
      id={isLanding ? 'explore' : undefined}
      className={
        isLanding
          ? // Height follows the cards themselves — no fixed height, so there's
            // no empty tail below them.
            'bg-[#cdc4c1] px-5 md:px-10 lg:px-22 4xl:px-50 pb-10 md:pb-14 md:flex md:flex-col scroll-mt-28'
          : 'bg-[#cdc4c1] pb-4 3xl:pb-10 pl-8 pr-2 md:pl-10 md:pr-2 lg:pl-16 lg:pr-3 4xl:pl-24 4xl:pr-4 min-h-screen md:h-screen md:max-h-screen md:overflow-hidden md:flex md:flex-col'
      }
      // `initial={false}`: render straight in the "visible" state. With
      // initial="hidden" the server HTML carried opacity:0 and the whole
      // section stayed invisible until the JS bundle arrived and hydrated —
      // seconds on a slow connection, forever if a chunk failed. The fade-in
      // is CSS now (see the `animate-in` classes on the cards), which runs
      // without JavaScript.
      initial={false}
      animate="visible"
      variants={containerVariants}
    >
      {/* Logo for dashboard view */}
      {!isLanding && (
        <div className="mb-[12px] md:mb-[6px] 3xl:mb-[10px] 3xl:mx-10 lg:mx-0 mx-10">
          <Logo
            showDescription={false}
            className="w-full h-[18vh] 3xl:h-[14vh] md:h-[9vh] mx-auto"
          />
        </div>
      )}

      <div className="w-full mx-auto sm:px-0 md:flex-1 md:min-h-0 md:flex md:flex-col md:justify-end md:pb-3 lg:pb-4">
        <motion.div
          className={`flex ${isLanding ? 'mx-10 lg:mx-8 md:mx-0 3xl:mx-50' : 'mx-2 lg:mx-0 md:mx-0 3xl:mx-0'} 3xl:justify-between lg:justify-around justify-between gap-10 lg:gap-15 4xl:gap-30 ${isLanding ? 'mt-4' : 'max-sm:-mt-20 -mt-10'} flex-col md:flex-row md:h-[100%] md:max-h-[100%] md:min-h-0 `}
          variants={containerVariants}
        >
          {/* Community Card */}
          <motion.a
            className="flex flex-col cursor-pointer w-full md:w-[40%] 3xl:w-[40%] h-full md:min-h-0 relative animate-in fade-in-0 slide-in-from-bottom-4 duration-700 fill-mode-both"
            style={{ animationDelay: '0ms' }}
            {...cardLink('/public/forums', '/forums?tab=posts')}
            variants={cardVariants}
            whileHover="hover"
            whileTap="tap"
            {...hoverVariants}
          >
            <motion.div className="overflow-hidden w-full aspect-square md:h-auto md:flex-shrink-0 relative">
              <motion.div
                variants={imageVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 w-full h-full"
              >
                <img
                  src="/community.webp"
                  alt="community"
                  className="w-full h-full object-contain object-top"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </motion.div>

            {/* No negative top margin here: community.webp is square and its
                box is aspect-square, so the image leaves no slack to reclaim —
                pulling the text up just slides it under the image, which paints
                on top (its wrapper is positioned, this one isn't). */}
            <motion.div variants={textVariants}>
              <motion.h2
                className={`mb-3 md:mb-1 lg:mb-2 3xl:mb-5 ${titleSizeCls} ${cinzel.className} `}
              >
                COMMUNITY
              </motion.h2>
              <motion.p
                className={`${merriweather.className} mb-3 md:mb-1 lg:mb-2 3xl:mb-5 ${bodySizeCls} `}
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <span className="font-bold">Ask. Answer. Connect.</span> Post a
                question, reply to another parent, share what&apos;s working
                (and what isn&apos;t). Start a group around your neighborhood,
                your stage, or your special thing. The community is only as good
                as what you put into it.
              </motion.p>
            </motion.div>
          </motion.a>

          {/* Grid Cards */}
          <motion.div
            className="w-full grid grid-cols-1 md:grid-cols-2 md:auto-rows-min md:content-start gap-x-6 2xl:gap-x-12 md:gap-y-9 3xl:gap-y-12 gap-y-4 md:w-[55%] h-full md:min-h-0"
            variants={gridVariants}
          >
            {/* Marketplace Card */}
            <motion.a
              className="cursor-pointer overflow-hidden flex flex-col h-full md:min-h-0 animate-in fade-in-0 slide-in-from-bottom-4 duration-700 fill-mode-both"
              style={{ animationDelay: '120ms' }}
              {...cardLink('/public/marketplace', '/marketplace/items')}
              variants={cardVariants}
              whileHover="hover"
              whileTap="tap"
              {...hoverVariants}
            >
              <motion.div
                className={`relative overflow-hidden aspect-square w-full ${isLanding ? 'md:aspect-[5/3] md:h-auto' : 'md:aspect-auto md:h-[38%] md:flex-shrink-0'} md:min-h-0`}
              >
                <motion.div
                  variants={imageVariants}
                  whileHover="hover"
                  className="w-full h-full"
                >
                  <motion.img
                    src="/market.webp"
                    alt="marketplace"
                    className="object-cover w-full h-full transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 768px) 50vw, 100vw"
                    whileHover={{ scale: 1.05 }}
                  />
                </motion.div>
              </motion.div>
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <motion.h2
                  className={`mb-1.5 3xl:mb-1.5 md:mb-1 ${titleSizeCls} ${cinzel.className}`}
                >
                  marketplace
                </motion.h2>
                <p className={`${merriweather.className} ${bodySizeCls} `}>
                  <span className="font-bold">Buy. Sell. Trade.</span> Pass it
                  on. List the stroller you&apos;re done with. Snag a barely
                  used highchair. Recommend the nanny who changed your life. No
                  fees, no middlemen just parents helping parents.
                </p>
              </motion.div>
            </motion.a>

            {/* Education Card */}
            <motion.a
              className="cursor-pointer  overflow-hidden flex flex-col h-full md:min-h-0 animate-in fade-in-0 slide-in-from-bottom-4 duration-700 fill-mode-both"
              style={{ animationDelay: '240ms' }}
              {...cardLink('/public/education', '/education')}
              variants={cardVariants}
              whileHover="hover"
              whileTap="tap"
              {...hoverVariants}
            >
              <motion.div
                className={`relative overflow-hidden aspect-square w-full ${isLanding ? 'md:aspect-[5/3] md:h-auto' : 'md:aspect-auto md:h-[38%] md:flex-shrink-0'} md:min-h-0`}
              >
                <motion.div
                  variants={imageVariants}
                  whileHover="hover"
                  className="h-full w-full"
                >
                  <motion.img
                    src="/edu.webp"
                    alt="education"
                    className="object-cover w-full  h-full transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 768px) 50vw, 100vw"
                    whileHover={{ scale: 1.05 }}
                  />
                </motion.div>
              </motion.div>
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <motion.h2
                  className={`mb-1.5 3xl:mb-1.5 md:mb-1 ${titleSizeCls} ${cinzel.className} `}
                >
                  education
                </motion.h2>
                <p className={`${merriweather.className} ${bodySizeCls} `}>
                  <span className="font-bold">Research. Review. Compare.</span>{' '}
                  Look up your school, scout daycares, read what other parents
                  really think. Been somewhere? Share insights with your Amici - the next parent is searching right now.
                </p>
              </motion.div>
            </motion.a>

            {/* Businesses Card */}
            <motion.a
              className="cursor-pointer overflow-hidden duration-300 flex flex-col h-full md:min-h-0 animate-in fade-in-0 slide-in-from-bottom-4 duration-700 fill-mode-both"
              style={{ animationDelay: '360ms' }}
              {...cardLink('/public/businesses', '/businesses')}
              variants={cardVariants}
              whileHover="hover"
              whileTap="tap"
              {...hoverVariants}
            >
              <motion.div
                className={`relative overflow-hidden aspect-square w-full ${isLanding ? 'md:aspect-[5/3] md:h-auto' : 'md:aspect-auto md:h-[38%] md:flex-shrink-0'} md:min-h-0`}
              >
                <motion.div
                  variants={imageVariants}
                  whileHover="hover"
                  className="h-full w-full"
                >
                  <motion.img
                    src="/bui.webp"
                    alt="businesses"
                    className="object-cover w-full h-full transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 768px) 50vw, 100vw"
                    whileHover={{ scale: 1.05 }}
                  />
                </motion.div>
              </motion.div>
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
              >
                <motion.h2
                  className={`mb-1.5 3xl:mb-1.5 md:mb-1 ${titleSizeCls} ${cinzel.className} `}
                >
                  businesses
                </motion.h2>
                <p className={`${merriweather.className} ${bodySizeCls} `}>
                  <span className="font-bold">Discover. Vet. Vouch.</span> Find
                  the music class, the swim school, the pediatric dentist that
                  doesn&apos;t make your kid cry. Share insights with your Amici about the businesses you know. Save your favorites. Add who&apos;s
                  missing.
                </p>
              </motion.div>
            </motion.a>

            {/* Activities Card - Coming Soon */}
            <motion.a
              className="cursor-pointer overflow-hidden opacity-60 relative flex flex-col h-full md:min-h-0 animate-in fade-in-0 slide-in-from-bottom-4 duration-700 fill-mode-both"
              style={{ animationDelay: '480ms' }}
              variants={cardVariants}
              {...cardLink('/public/happenings', '/happenings')}
            >
              <motion.div
                className={`relative overflow-hidden aspect-square w-full ${isLanding ? 'md:aspect-[5/3] md:h-auto' : 'md:aspect-auto md:h-[38%] md:flex-shrink-0'} md:min-h-0`}
              >
                <motion.img
                  src="/act.webp"
                  alt="activities"
                  className="object-cover w-full h-full transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  sizes="(min-width: 768px) 50vw, 100vw"
                  whileHover={{ scale: 1.05 }}
                />
              </motion.div>
              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                <h2
                  className={`mb-1.5 3xl:mb-1.5 md:mb-1 ${titleSizeCls} ${cinzel.className} `}
                >
                  happenings
                </h2>
                <p className={`${merriweather.className} ${bodySizeCls}`}>
                  <span className="font-bold">Show up. Round up. RSVP.</span>{' '}
                  Discover what&apos;s on this weekend, organize a meet-up, post
                  the playdate at your park. The best memories are made offline
                  — we help you find each other.
                </p>
              </motion.div>
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
}
